/**
 * CitationChip — the inline "Источник" citation chip + its article BottomSheet
 * (design revamp).
 *
 * Under a grounded assistant answer the design shows a small pill (book icon +
 * "Источник") that opens a slide-over with the cited KB article
 * (`screens-chat.jsx` → `BottomSheet` reading `GARDEN_ARTICLE`). In production
 * the article is fetched live from the KB endpoint via `getArticle(slug, lang)`
 * (`@/lib/garden-api`), keyed in TanStack Query and fetched only once the sheet
 * is opened.
 *
 * The sheet renders the article title + heading blocks (`{h, p}`) and callouts
 * (`{callout, text}`), with a CTA to ask the assistant about it (re-sends the
 * article title as a chat message). States: loading (Skeleton), error (retry),
 * and content. All copy is i18n (`chat` namespace); article text is DB text
 * rendered AS text (never HTML — no `dangerouslySetInnerHTML`). Tokens via the
 * `--g-*` mirror.
 */
import { Button, Icon, Pill, Skeleton, BottomSheet } from "@garden/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  getArticle,
  isCalloutBlock,
  type KbArticleDetail,
} from "@/lib/garden-api";
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/lib/i18n";
import type { CitationEvent } from "@/features/chat/useChatStream";

/** TanStack query key for one KB article (slug + lang). */
export function articleQueryKey(
  slug: string,
  lang: SupportedLanguage,
): readonly unknown[] {
  return ["kb", "article", slug, lang];
}

/** Narrow the live i18n language to a supported article language (RU default). */
function resolveLanguage(raw: string): SupportedLanguage {
  const base = raw.split("-")[0] ?? "ru";
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : "ru";
}

export interface CitationChipProps {
  citation: CitationEvent;
  /** Open the sheet on the desktop (right-side) variant. */
  desktop?: boolean;
  /** Re-send a message into the chat (used by the "ask about this" CTA). */
  onAsk?: (text: string) => void;
  /** Pill text. When several sources are shown for one turn (Phase 2 RAG),
   * pass the article title so the chips are distinguishable; defaults to the
   * generic "Источник" label (the single-source / quote-review case). */
  label?: string;
}

export function CitationChip({
  citation,
  desktop = false,
  onAsk,
  label,
}: CitationChipProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const lang = resolveLanguage(i18n.language);

  const articleQuery = useQuery({
    queryKey: articleQueryKey(citation.slug, lang),
    queryFn: (): Promise<KbArticleDetail> => getArticle(citation.slug, lang),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <Pill
        tone="primary"
        size="sm"
        leading="book"
        onClick={() => setOpen(true)}
        className="mt-2 max-w-full align-middle"
      >
        <span
          data-testid="chat-citation-chip"
          className="inline-block max-w-[200px] truncate align-middle"
        >
          {label ?? t("source")}
        </span>
      </Pill>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={citation.title}
        desktop={desktop}
      >
        <div data-testid="chat-citation-sheet">
          {articleQuery.isLoading ? (
            <div className="flex flex-col gap-3" data-testid="chat-citation-loading">
              <Skeleton w="66%" h={18} />
              <Skeleton w="100%" h={14} />
              <Skeleton w="100%" h={14} />
              <Skeleton w="84%" h={14} />
            </div>
          ) : articleQuery.isError ? (
            <div data-testid="chat-citation-error">
              <p className="text-[15px]/[1.429] text-destructive">
                {t("errors.generic")}
              </p>
              <Button
                variant="secondary"
                full
                className="mt-3"
                onClick={() => void articleQuery.refetch()}
              >
                {t("citation.open")}
              </Button>
            </div>
          ) : articleQuery.data ? (
            (() => {
              const article = articleQuery.data;
              return (
                <ArticleBody
                  article={article}
                  onAsk={
                    onAsk
                      ? () => {
                          setOpen(false);
                          onAsk(article.title);
                        }
                      : undefined
                  }
                  askLabel={t("citation.ask")}
                />
              );
            })()
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
}

/** The rendered article body: title is already the sheet title; show the first
 * two heading blocks (the design slices `body.slice(0, 2)`), then a CTA. */
function ArticleBody({
  article,
  onAsk,
  askLabel,
}: {
  article: KbArticleDetail;
  onAsk?: () => void;
  askLabel: string;
}): ReactElement {
  // The design renders only the leading content blocks in the slide-over.
  const blocks = article.body.slice(0, 3);
  return (
    <div>
      {blocks.map((block, i) =>
        isCalloutBlock(block) ? (
          <div
            key={i}
            className={
              "mb-3 flex items-start gap-2 rounded-[var(--g-radius)] p-3 text-[14.5px] leading-relaxed " +
              (block.callout === "warn"
                ? "bg-[var(--g-warn)]/10 text-[color:var(--g-warn)]"
                : "bg-secondary text-secondary-foreground")
            }
          >
            <Icon
              name={block.callout === "warn" ? "warn" : "info"}
              size={15}
              className="mt-0.5 shrink-0"
            />
            <span>{block.text}</span>
          </div>
        ) : (
          <div key={i} className="mb-3">
            <div className="mb-1 text-[15px]/[1.429] font-bold text-foreground">
              {block.h}
            </div>
            <p className="text-[15px] leading-relaxed text-muted-foreground">
              {block.p}
            </p>
          </div>
        ),
      )}
      {onAsk ? (
        <Button
          variant="secondary"
          full
          trailing="arrowR"
          onClick={onAsk}
          className="mt-1"
        >
          {askLabel}
        </Button>
      ) : null}
    </div>
  );
}

export default CitationChip;
